import styled, { keyframes } from 'styled-components'

const savingSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const SavingOverlay = styled.div`
  position: fixed;
  z-index: 9999;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(4px);
`

export const SavingCard = styled.div`
  width: min(390px, 100%);
  padding: 24px;
  border: 1px solid #ececec;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #fff;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.14);
  text-align: center;

  strong {
    color: #262626;
    font-size: 16px;
  }

  span {
    color: #777;
    font-size: 12px;
    line-height: 1.5;
  }
`

export const SavingSpinner = styled.span`
  width: 34px;
  height: 34px;
  border: 4px solid #f2c5c5;
  border-top-color: #d60000;
  border-radius: 50%;
  animation: ${savingSpin} 0.8s linear infinite;
`

type CoverHeroProps = {
  $cover?: string
}

export const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 18px 28px 56px;
  background: #f7f7f6;
  color: #232323;

  @media (max-width: 760px) {
    padding: 14px 12px 40px;
  }
`

export const Loading = styled.div`
  min-height: 55vh;
  padding: 60px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
  font-size: 14px;
`

export const LoadingCard = styled.div`
  width: min(430px, 100%);
  padding: 24px;
  border: 1px solid #ececec;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: #fff;
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.08);
  text-align: center;

  > svg {
    color: #d60000;
    font-size: 24px;
  }

  strong {
    color: #262626;
    font-size: 16px;
  }

  span {
    color: #777;
    font-size: 12px;
    line-height: 1.5;
  }
`

export const LoadingSpinner = styled.span`
  width: 34px;
  height: 34px;
  border: 4px solid #f2c5c5;
  border-top-color: #d60000;
  border-radius: 50%;
  animation: ${savingSpin} 0.8s linear infinite;
`

export const LoadingActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;

  button {
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid #dedede;
    border-radius: 9px;
    background: #fff;
    color: #4b4b4b;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
  }

  button.primary {
    border-color: #eb0010;
    background: #eb0010;
    color: #fff;
  }

  @media (max-width: 520px) {
    width: 100%;
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

export const Form = styled.form`
  width: min(1500px, 100%);
  margin: 0 auto;
`

export const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;

  h1 {
    margin: 0;
    color: #252525;
    font-size: 29px;
    line-height: 1;
  }

  @media (max-width: 760px) {
    flex-direction: column;
  }
`

export const RouteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 7px;
  color: #5f5f5f;
  font-size: 12px;
`

export const CollabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  padding: 0 10px;
  border-radius: 999px;
  background: #fff0f0;
  color: #d60000;
  font-size: 10px;
  font-weight: 800;
`

export const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 540px) {
    width: 100%;
    flex-direction: column;
  }
`

const ActionButton = styled.button`
  min-height: 42px;
  padding: 0 17px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;

  @media (max-width: 540px) {
    width: 100%;
  }
`

export const PrimaryButton = styled(ActionButton)`
  border: 1px solid #eb0010;
  background: #eb0010;
  color: #fff;
`

export const SecondaryButton = styled(ActionButton)`
  border: 1px solid #dedede;
  background: #fff;
  color: #4b4b4b;
`

export const CoverHero = styled.div<CoverHeroProps>`
  position: relative;
  width: 100%;
  height: 245px;
  border-radius: 16px;
  overflow: hidden;
  background-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.16),
      rgba(0, 0, 0, 0)
    ),
    url(${({ $cover }) => $cover});
  background-size: cover;
  background-position: center;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);

  @media (max-width: 760px) {
    height: 190px;
  }
`

export const CoverActions = styled.div`
  position: absolute;
  right: 16px;
  bottom: 14px;
  display: flex;
  gap: 8px;

  label,
  button {
    min-height: 36px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.78);
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(24, 24, 24, 0.72);
    color: #fff;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
    backdrop-filter: blur(6px);
  }

  input {
    display: none;
  }

  @media (max-width: 520px) {
    left: 12px;
    right: 12px;

    label,
    button {
      flex: 1;
      justify-content: center;
    }
  }
`

export const Workspace = styled.div`
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 18px;
  margin-top: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const Sidebar = styled.aside`
  position: sticky;
  top: 78px;
  align-self: start;
  padding: 8px;
  border: 1px solid #ececec;
  border-radius: 15px;
  background: #fff;

  button {
    width: 100%;
    min-height: 42px;
    padding: 0 12px;
    border: 0;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    color: #4e4e4e;
    font-family: inherit;
    font-size: 11px;
    text-align: left;
    cursor: pointer;
  }

  button:hover {
    background: #fff2f2;
    color: #e00000;
  }

  @media (max-width: 900px) {
    position: static;
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    button {
      justify-content: center;
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const Main = styled.main`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const Section = styled.section`
  scroll-margin-top: 80px;
  padding: 18px;
  border: 1px solid #ececec;
  border-radius: 16px;
  background: #fff;
`

export const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 15px;

  h2 {
    margin: 0;
    color: #262626;
    font-size: 18px;
  }

  p {
    margin: 3px 0 0;
    color: #8a8a8a;
    font-size: 10px;
    line-height: 1.45;
  }

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

export const AddDayButton = styled.button`
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid #ed0012;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #ed0012;
  color: #fff;
  font-family: inherit;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 6px 15px rgba(237, 0, 18, 0.16);
`

export const InspirationSourceNotice = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #f0d3a5;
  border-radius: 12px;
  background: #fffaf1;
  color: #5b4a32;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
  }

  div {
    min-width: 0;
    flex: 1;
    display: grid;
    gap: 3px;
  }

  strong {
    font-size: 11px;
  }

  span {
    font-size: 10px;
    line-height: 1.45;
  }

  button {
    flex: 0 0 auto;
    min-height: 34px;
    padding: 0 11px;
    border: 1px solid #d8b77f;
    border-radius: 9px;
    background: #fff;
    color: #6d5330;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;

    button {
      width: 100%;
    }
  }
`

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  .wide {
    grid-column: span 2;
  }

  .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;

    .wide,
    .full {
      grid-column: auto;
    }
  }
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    color: #4b4b4b;
    font-size: 10px;
    font-weight: 800;
  }

  input,
  textarea {
    width: 100%;
    border: 1px solid #dedede;
    border-radius: 9px;
    background: #fff;
    color: #333;
    font-family: inherit;
    font-size: 11px;
    outline: none;
  }

  input {
    min-height: 40px;
    padding: 0 11px;
  }

  textarea {
    min-height: 94px;
    padding: 10px 11px;
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    border-color: #d90000;
    box-shadow: 0 0 0 3px rgba(217, 0, 0, 0.06);
  }
`

export const PillGroup = styled.div`
  display: flex;
  gap: 7px;

  button {
    min-height: 40px;
    padding: 0 16px;
    border: 1px solid #dedede;
    border-radius: 999px;
    background: #fff;
    color: #666;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
  }

  button.active {
    border-color: #e00000;
    background: #fff2f2;
    color: #d50000;
  }
`

export const DaysList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`

export const DayCard = styled.article`
  border: 1px solid #e9e9e9;
  border-radius: 14px;
  overflow: visible;
  background: #fff;
`

export const DayHeader = styled.div`
  min-height: 52px;
  padding: 8px 10px;
  border-bottom: 1px solid #efefef;
  display: grid;
  grid-template-columns: auto 145px minmax(170px, 360px) 36px;
  align-items: center;
  gap: 10px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr 1fr;

    > *:last-child {
      justify-self: end;
    }
  }
`

export const DayNumber = styled.strong`
  min-width: 78px;
  color: #292929;
  font-size: 13px;

  span {
    display: block;
    max-width: 150px;
    margin-top: 3px;
    overflow: hidden;
    color: #e00000;
    font-size: 9px;
    font-weight: 700;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const DayDate = styled.div`
  min-height: 35px;
  padding: 0 8px;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #777;

  input {
    width: 100%;
    border: 0;
    background: transparent;
    color: #4d4d4d;
    font-family: inherit;
    font-size: 10px;
    outline: none;
  }
`

export const DayTitleInput = styled.input`
  min-height: 35px;
  padding: 0 10px;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  color: #454545;
  font-family: inherit;
  font-size: 10px;
  outline: none;

  &:focus {
    border-color: #d90000;
  }
`

export const DeleteDayButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid #ffd5d5;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff8f8;
  color: #e00000;
  cursor: pointer;
`

export const DayCities = styled.div`
  display: flex;
  flex-direction: column;
`

export const CityEditorRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 145px minmax(190px, 1.05fr) minmax(220px, 0.95fr) minmax(
      260px,
      1.4fr
    );
  gap: 12px;
  align-items: stretch;
  padding: 10px 42px 10px 10px;
  border-bottom: 1px solid #f0f0f0;

  @media (max-width: 1100px) {
    grid-template-columns: 130px 1fr 1fr;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    padding-right: 10px;
  }
`

export const CityCover = styled.div`
  position: relative;
  width: 100%;
  height: 108px;
  align-self: start;
  border-radius: 10px;
  overflow: hidden;
  background: #f0f0f0;

  > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  > label {
    position: absolute;
    left: 7px;
    bottom: 7px;
    min-height: 25px;
    padding: 0 8px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: rgba(20, 20, 20, 0.72);
    color: #fff;
    font-size: 8px;
    font-weight: 800;
    cursor: pointer;
  }

  input {
    display: none;
  }

  @media (max-width: 1100px) {
    height: 100px;
  }

  @media (max-width: 760px) {
    height: 180px;
  }
`

export const CityCoverEmpty = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #a0a0a0;
  font-size: 9px;

  svg {
    font-size: 17px;
  }
`

export const CityField = styled.div`
  min-width: 0;
`

export const GalleryField = styled.div`
  min-width: 0;

  @media (max-width: 1100px) {
    grid-column: 3;
  }

  @media (max-width: 760px) {
    grid-column: auto;
  }
`

export const NotesField = styled.div`
  min-width: 0;
  position: relative;

  textarea {
    width: 100%;
    min-height: 72px;
    padding: 9px 10px 18px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    color: #4a4a4a;
    font-family: inherit;
    font-size: 10px;
    line-height: 1.45;
    resize: vertical;
    outline: none;
  }

  textarea:focus {
    border-color: #d90000;
  }

  small {
    position: absolute;
    right: 7px;
    bottom: 5px;
    color: #999;
    font-size: 8px;
  }

  @media (max-width: 1100px) {
    grid-column: 2 / -1;
  }

  @media (max-width: 760px) {
    grid-column: auto;
  }
`

export const MiniLabel = styled.div`
  margin-bottom: 6px;
  color: #555;
  font-size: 9px;
  font-weight: 800;
`

export const CitySearchWrap = styled.div`
  position: relative;
  min-height: 39px;
  border: 1px solid #dfdfdf;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 9px;
  color: #8a8a8a;

  input {
    min-width: 0;
    flex: 1;
    border: 0;
    background: transparent;
    color: #3f3f3f;
    font-family: inherit;
    font-size: 10px;
    outline: none;
  }

  > button {
    border: 0;
    background: transparent;
    color: #888;
    cursor: pointer;
  }
`

export const CityOptions = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);

  button {
    width: 100%;
    min-height: 38px;
    padding: 7px 10px;
    border: 0;
    border-bottom: 1px solid #f2f2f2;
    background: #fff;
    color: #444;
    font-family: inherit;
    font-size: 9px;
    text-align: left;
    cursor: pointer;
  }

  button:hover {
    background: #fff7f7;
  }
`

export const Searching = styled.span`
  position: absolute;
  top: calc(100% + 4px);
  left: 4px;
  color: #999;
  font-size: 8px;
`

export const MiniGallery = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

export const GalleryThumb = styled.div`
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 8px;
  overflow: hidden;
  background: #f2f2f2;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  button {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 17px;
    height: 17px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(25, 25, 25, 0.72);
    color: #fff;
    font-size: 7px;
    cursor: pointer;
  }
`

export const AddPhotoLabel = styled.label`
  width: 54px;
  height: 54px;
  border: 1px dashed #cfcfcf;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #777;
  cursor: pointer;

  input {
    display: none;
  }
`

export const MorePhotos = styled.span`
  color: #777;
  font-size: 9px;
  font-weight: 800;
`

export const RemoveCityButton = styled.button`
  position: absolute;
  top: 10px;
  right: 9px;
  width: 27px;
  height: 27px;
  border: 1px solid #ffd8d8;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff8f8;
  color: #e00000;
  font-size: 10px;
  cursor: pointer;
`

export const AddCityButton = styled.button`
  width: calc(100% - 20px);
  min-height: 34px;
  margin: 8px 10px 10px;
  border: 1px dashed #ed8f96;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: #fff;
  color: #e00000;
  font-family: inherit;
  font-size: 9px;
  font-weight: 800;
  cursor: pointer;
`

export const InterestGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    min-height: 34px;
    padding: 0 12px;
    border: 1px solid #dedede;
    border-radius: 999px;
    background: #fff;
    color: #666;
    font-family: inherit;
    font-size: 9px;
    font-weight: 700;
    cursor: pointer;
  }

  button.active {
    border-color: #e00000;
    background: #fff1f1;
    color: #d60000;
  }
`

export const Collaborators = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 14px;
`

export const Collaborator = styled.div`
  display: grid;
  grid-template-columns: 38px 1fr auto;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #fafafa;

  img {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    object-fit: cover;
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: #333;
    font-size: 11px;
  }

  span {
    color: #8a8a8a;
    font-size: 9px;
  }

  > button {
    border: 0;
    background: transparent;
    color: #c60000;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
    cursor: pointer;
  }
`

export const StickySaveBar = styled.div`
  position: sticky;
  z-index: 35;
  bottom: 12px;
  width: calc(100% - 198px);
  margin: 16px 0 0 auto;
  padding: 10px 12px;
  border: 1px solid #e7e7e7;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(10px);

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: #2b2b2b;
    font-size: 11px;
  }

  span {
    color: #8b8b8b;
    font-size: 9px;
  }

  @media (max-width: 900px) {
    width: 100%;
  }

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;

    > div:first-child {
      display: none;
    }
  }
`

export const StickyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`
